import { useCallback, useEffect, useMemo, useState } from 'react';
import { type ChannelFilters } from 'stream-chat';
import { useChatContext } from 'stream-chat-react';
import { MdOutlineFilterAlt } from 'react-icons/md';

import { IconButton } from '@ds';
import { getChannelsSort } from '@features/components/Chat/components/ChatContent/utils';
import { ChatMessages } from '@features/components/Chat/components/ChatContent/Messages';
import { CustomEmptyMessagesState } from '@features/components/Chat/components/ChatContent/Messages/EmptyComponents/CustomEmptyMessagesState';
import {
  getClosedChatMessageForChannel,
  isChannelFrozen,
  isChannelInCase,
  isChannelReadOnly,
} from '@features/components/Chat/utils';
import { useChatPanelStore } from '@features/store/chat-panel.store';
import { DEFAULT_CHAT_FILTERS } from '../../constants';
import { UseChatFiltersReturn } from '../../types';

import { useRestoreActiveChannel } from './useRestoreActiveChannel';
import { ChatChannelList } from './ChatChannelList';
import { AppliedFiltersBar } from './Filters/AppliedFiltersBar';
import { ChatFilterPanel } from './Filters/ChatFilterPanel';

type Props = {
  caseId?: string;
  streamChatFilters?: ChannelFilters;
  inlineLayout?: boolean;
  filterState?: UseChatFiltersReturn;
};

export const ChatContent = ({ caseId, streamChatFilters, inlineLayout = false, filterState }: Props) => {
  const {
    filters: uiFilters,
    draftFilters,
    version: filterVersion,
    ...filterActions
  } = filterState ?? DEFAULT_CHAT_FILTERS;

  // const { type } = useAppContext();
  const { channel: contextChannel, setActiveChannel } = useChatContext();
  const { saveActiveChannel, clearActiveChannel } = useChatPanelStore();

  // const userRole = client?.user?.role;
  // const hasFullAccess = type === AppType.NopConnectPlatform && hasChatsFullAccess(userRole);

  // The stream-chat active channel is global; never render one that belongs to another case
  const activeChannel = isChannelInCase(contextChannel, caseId) ? contextChannel : undefined;

  const isChatReadOnly = isChannelFrozen(activeChannel) || isChannelReadOnly(activeChannel);

  const handleBack = useCallback(() => {
    setActiveChannel(undefined);
    clearActiveChannel(caseId);
  }, [caseId, clearActiveChannel, setActiveChannel]);

  const handleChannelSelect = useCallback(
    (channel: { id?: string; type: string }) => {
      if (!channel.id) return;
      saveActiveChannel({ id: channel.id, type: channel.type }, caseId);
    },
    [caseId, saveActiveChannel],
  );

  const readOnlyMessage = useMemo(
    () => (isChatReadOnly ? getClosedChatMessageForChannel(activeChannel) : undefined),
    [isChatReadOnly, activeChannel],
  );

  // Reset active channel when case or filters change; restore persisted channel for the new context
  useEffect(() => {
    setActiveChannel(undefined);
  }, [caseId, filterVersion, setActiveChannel]);

  // Restore persisted active channel after reset
  useRestoreActiveChannel({ caseId, filterVersion, streamChatFilters });

  const [isFilterOpen, setFilterOpen] = useState(false);
  const toggleFilterPanel = () => setFilterOpen((v) => !v);

  const sortOption = useMemo(() => getChannelsSort(uiFilters.unreadOnly), [uiFilters.unreadOnly]);

  // Case chats
  if (!inlineLayout) {
    return (
      <div className="w-full h-full flex flex-col">
        {activeChannel ? (
          <ChatMessages onBack={handleBack} isReadOnly={isChatReadOnly} readOnlyMessage={readOnlyMessage} />
        ) : (
          <ChatChannelList
            streamChatFilters={streamChatFilters}
            sortOption={sortOption}
            caseId={caseId}
            uiFilters={uiFilters}
            inlineLayout={inlineLayout}
            onChannelSelect={handleChannelSelect}
          />
        )}
      </div>
    );
  }

  // Chat hub
  return (
    <div className="w-full h-full flex">
      <div className="w-[340px] border-r border-base-60 flex flex-col bg-base-80 scroll no-track">
        <div className="flex border-b border-base-60 flex-col">
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-xl-sb">Chats</span>
            <IconButton
              size="sm"
              variant="light"
              startContent={<MdOutlineFilterAlt />}
              onPress={toggleFilterPanel}
              aria-label="Toggle Filters"
            />
          </div>
        </div>

        <AppliedFiltersBar draftFilters={uiFilters} setAndApplyFilter={filterActions.setAndApplyFilter} />

        <ChatChannelList
          streamChatFilters={streamChatFilters}
          sortOption={sortOption}
          caseId={caseId}
          uiFilters={uiFilters}
          inlineLayout={inlineLayout}
          onChannelSelect={handleChannelSelect}
        />
      </div>

      {isFilterOpen && <ChatFilterPanel draftFilters={draftFilters} {...filterActions} onClose={toggleFilterPanel} />}

      <div className="flex-1 h-full">
        {activeChannel ? (
          <ChatMessages
            onBack={handleBack}
            isReadOnly={isChatReadOnly}
            readOnlyMessage={readOnlyMessage}
            inlineLayout
          />
        ) : (
          <CustomEmptyMessagesState />
        )}
      </div>
    </div>
  );
};
